import {
    PlaneCanvas,
    Plane,
    FireCell,
    isFireCellProps,
    SceneCreator,
    type SceneTerrain,
    WindDirections,
    calcWindChange,
    getFireState,
    FireDanger,
    WindSpeed,
    WindConvention,
    isButtonElement,
    enableAdminMode,
    CellProps,
    addCellInspectorToggle,
    getBoolFromParams,
    setupCounter,
    launchTerrainTweakerModal,
    getConstants,
    setupControls,
    setupSpeedControls,
    setupInstructionDialog,
    supportsCSSTransformsOnSVG,
    Controls,
    setupDrawButton,
    injectTerrainTweakerStyles
} from "@firesim/simkit-core"
import { bindLoadButton } from "./loadRuralBlockData";
import { hideHouseBurnedWarning, enableWarning } from "./house-burned-warning";
import { WindSliderComponent } from "@firesim/ui-components";
import {
  SmokeWorkerManager,
  simkitWindTodegrees,
  simkitWindSpeedToNormalized,
  windDirectionToVectorCached,
} from "@firesim/smoke-simulation";

import { setupPlaceIgnitionButton } from "./draw-ignition";
import type { Config } from "./config";
import { TerrainCodes, terrainCodeValues, terrainTypes } from "./terrain-types";
import { parseAutomationParams, applyAutomationParams } from "./url-params";
import { placeIgnitionAt } from "./place-ignition-at";

import selectRegion from "./controls/select-region";
import growGamba from "./controls/grow-gamba";
import { setupSmokeToggle } from "./controls/smoke-toggle";
import { setupSpeedPopover } from "./controls/speed-popover";

import './constants';

export const config: Config = {
    planeData: "data/darwin/houses.json",
    backgroundImage: "images/darwin/houses.png",
    backgroundColor: "#8faa7a",
    buildings: [
    ],
    roads: [],
    trees: [
    ],
    terrain: [
    ],
};

document.addEventListener("DOMContentLoaded", async () => {
  if (!supportsCSSTransformsOnSVG()) {
    const controls = document.querySelector(".controls");
    if (controls) {
      controls.classList.remove("svg-supported");
    }
  }

  const firecellFactory = (props: CellProps<TerrainCodes, FireCell<TerrainCodes>>) => {
      if(isFireCellProps(props)) {
          return new FireCell(props);
      }
      return new FireCell({
          fuelLoad: 0,
          ...props
      });
  }


  const sceneCreator = new SceneCreator<FireCell<TerrainCodes>, TerrainCodes>(
    firecellFactory,
  );
  const cellData = await sceneCreator.loadTerrainFromJson(config.planeData, terrainCodeValues);
  const cells = sceneCreator.loadCellsFromData(cellData, terrainTypes);

  const canvas = document.getElementById("canvas") as HTMLCanvasElement;

  const layers = [
    "background",
    "sprites",
    "main",
    "buildings",
    "smoke",
    "fire",
    "effects",
  ];
  const canvasDrawer = new PlaneCanvas(canvas, { layers });
  const plane = new Plane<TerrainCodes, FireCell<TerrainCodes>>(
    cells,
    canvasDrawer,
  );
  cells.flat().forEach((cell) => cell.plane = plane);


  if(config.backgroundImage) {
      sceneCreator.loadImage(config.backgroundImage).then((image) => {
            //plane.loadImage(image, {layer: 'background', scaleCanvasToImage: false, scaleToCanvas: true});
            plane.loadImage(image, {layer: 'background', scaleCanvasToImage: true});
      });
  }

  // Ensure legacy-behaviour agents (Darwin*) run first in a defined order.
  // Keep core names as fallback for compatibility.
  plane.agentRunOrder = ["DarwinFrontAgent", "DarwinEmberAgent", "FrontAgent", "EmberAgent"];

  const controls = new Controls(plane);

  const state = getFireState(plane);

  state.setWindConvention(WindConvention.Toward);
  state.setWindDirection(WindDirections.N);

  plane.addTickPrerunner(() => {
    let currentWindSpeed = state.currentWindSpeed;
    let windSpeed = state.windSpeed;
    state.setCurrentWindSpeed(calcWindChange(currentWindSpeed, windSpeed));
    state.setThrowDistance(0);
  });

  controls.fireDanger = FireDanger.Medium;
  controls.windSpeed = WindSpeed.Light;
  controls.windDirection = WindDirections.N;

  // Wind semantics:
  // - UI presents wind as the direction it comes FROM.
  // - simkit-core currently expects the direction the wind moves TO.
  //   The UI layer in darwin.pug inverts option values accordingly.
  setupControls(plane);

  // Setup wind slider component
  const windSlider = new WindSliderComponent({
      sliderId: 'windSpeedInput',
      imageContainerId: 'windSliderImage',
      defaultValue: 2 // Start with Light wind
  });
    
  setupInstructionDialog();

  // Smoke simulation (v1 worker API), run on each simulation tick.
  const smokeToggleBtn = document.getElementById('smokeToggleBtn') as HTMLButtonElement | null;
  const smokeToggle = smokeToggleBtn
    ? setupSmokeToggle({
        button: smokeToggleBtn,
        initialEnabled: true,
        onChange: (enabled) => {
          if (!enabled) {
            smokeManager.clear();
            plane.clearRect(0, 0, plane.width, plane.height, 'smoke');
          }
        },
      })
    : null;
  const isSmokeEnabled = () => (smokeToggle ? smokeToggle.isEnabled() : true);

  const smokeManager = new SmokeWorkerManager({
      width: plane.width,
      height: plane.height,
      smokeConfig: {
          fadeRate: 0.02,
          windInfluence: 1.5,
          smokeOpacity: 0.02,
          smokeRadius: 3,
          performanceMode: 'high',
          enableBlur: true,
          blurRadius: 1.5
      }
  });

  smokeManager.onRender = (bitmap) => {
    if (!isSmokeEnabled()) {
      plane.clearRect(0, 0, plane.width, plane.height, 'smoke');
      bitmap.close();
      return;
    }

    plane.clearRect(0, 0, plane.width, plane.height, 'smoke');
    plane.loadImage(bitmap, { layer: 'smoke', scaleToCanvas: true, scaleCanvasToImage: false });
    bitmap.close();
  };

  const collectSmokeSources = () => {
    const activeAgents: Record<string, Set<any>> = (plane as any).activeAgents || {};
    const fireSources: { x: number; y: number; intensity: number }[] = [];

    for (const [agentType, agents] of Object.entries(activeAgents)) {
      if (!(agents instanceof Set) || agents.size === 0) {
        continue;
      }

      const key = agentType.toLowerCase();
      const isFront = key.includes('frontagent');
      const isEmber = key.includes('emberagent');
      if (!isFront && !isEmber) {
        continue;
      }

      const intensity = isFront ? 0.8 : 0.4;
      for (const agent of agents) {
        if (agent?.cell) {
          fireSources.push({ x: agent.cell.x, y: agent.cell.y, intensity });
        }
      }
    }

    return fireSources;
  };

  plane.addEventListener('tick', () => {
    const windDirection = state.windDirection;
    const windSpeed = state.currentWindSpeed || state.windSpeed;
    const degrees = simkitWindTodegrees(windDirection);
    const normalizedSpeed = simkitWindSpeedToNormalized(windSpeed);
    const windVector = windDirectionToVectorCached(degrees, normalizedSpeed);
    smokeManager.updateWind(windVector);

    if (!isSmokeEnabled()) {
      return;
    }

    smokeManager.render(collectSmokeSources());
  });


  plane.addEventListener('reset', () => {
    smokeManager.clear();
    plane.clearRect(0, 0, plane.width, plane.height, 'smoke');
  });

  window.addEventListener('beforeunload', () => {
    smokeManager.destroy();
  });

  // Hook up intro dialog close button (dialog is visible on first load)
  const introClose = document.getElementById('introCloseBtn');
  const dismissIntroDialog = () => {
    const introDialog = document.getElementById('introDialog');
    if (introDialog) {
      (introDialog as HTMLElement).style.display = 'none';
    }
  };

  if (introClose) {
    introClose.addEventListener('click', (e) => {
      e.preventDefault();
      dismissIntroDialog();
    });
  }

  enableAdminMode(plane);

  setupDrawButton({
      terrain: terrainTypes[TerrainCodes.GambaGrass],
      selector: '#drawGambaBtn',
      radius: 4,
      coverage: 0.3,
      plane,
      excludeTerrain: [TerrainCodes.House, TerrainCodes.Empty, TerrainCodes.GambaGrass]
  });

  setupDrawButton({
      terrain: terrainTypes[TerrainCodes.Break],
      selector: '#drawBreakBtn',
      radius: 2,
      coverage: 0.9,
      delayTime: 1,
      plane,
      excludeTerrain: [TerrainCodes.House, TerrainCodes.Empty]
  });

  // Native grass restoration - replaces gamba grass with native species
  setupDrawButton({
      terrain: terrainTypes[TerrainCodes.Grass],
      selector: '#drawGrassBtn',
      radius: 4,
      coverage: 0.3,
      plane,
      excludeTerrain: [TerrainCodes.House, TerrainCodes.Empty, TerrainCodes.Grass]
  });

	
	document.getElementById('sproutGambaBtn')?.addEventListener('click', (clickEvt) => {
		clickEvt.preventDefault();
		selectRegion(
			(region) => {
				const cells = plane.getCellsInRange(region.startX, region.endX, region.startY, region.endY);
                growGamba(cells);
			},
			{plane});
	});



  const ignitionBtn = document.getElementById("ignitionBtn");
  if (!isButtonElement(ignitionBtn)) {
    throw new Error("ignitionBtn is not a button element");
  }
  setupPlaceIgnitionButton(ignitionBtn, plane);

  bindLoadButton(
    document.getElementById("reloadBtn") as HTMLButtonElement,
    plane,
    () => {
      hideHouseBurnedWarning();
      enableWarning();

      plane.cells = sceneCreator.loadCellsFromData(cellData, terrainTypes);
      plane.cells.flat().forEach((cell) => cell.plane = plane);
      plane.clearRect(0, 0, plane.width, plane.height, 'main');

      if(config.backgroundImage) {
          sceneCreator.loadImage(config.backgroundImage).then((image) => {
                plane.loadImage(image, {layer: 'background', scaleCanvasToImage: true});
          });
      }


    },
  );

  if(getBoolFromParams('inspector')) {
      addCellInspectorToggle(plane);
  }
  if(getBoolFromParams('admin')) {
     injectTerrainTweakerStyles();
     launchTerrainTweakerModal(
          terrainTypes,
          TerrainCodes,
          getConstants()

     );
  }

  setupCounter({
      textElement: document.getElementById('tickCounter') as HTMLElement,
      plane,
      mode: "minutes",
      tickQuota: 10
  });

  setupSpeedControls({ plane, selector: '#simSpeedRange', baseSpeed: 50 });

  const simSpeedChip = document.getElementById('simSpeedChip') as HTMLButtonElement | null;
  const simSpeedChipValue = document.getElementById('simSpeedChipValue') as HTMLElement | null;
  const simSpeedPopover = document.getElementById('simSpeedPopover') as HTMLElement | null;
  const simSpeedPopoverValue = document.getElementById('simSpeedPopoverValue') as HTMLElement | null;
  const simSpeedRange = document.getElementById('simSpeedRange') as HTMLInputElement | null;

  if (simSpeedChip && simSpeedChipValue && simSpeedPopover && simSpeedPopoverValue && simSpeedRange) {
    setupSpeedPopover({
      chipButton: simSpeedChip,
      chipValueEl: simSpeedChipValue,
      popoverEl: simSpeedPopover,
      popoverValueEl: simSpeedPopoverValue,
      sliderEl: simSpeedRange,
      presetButtons: Array.from(document.querySelectorAll('.speed-popover__preset')) as HTMLButtonElement[],
    });
  }

  // Apply any automation/screenshot URL parameters last, after all setup.
  const automationParams = parseAutomationParams();
  applyAutomationParams(automationParams, {
      controls,
      plane,
      placeIgnition: placeIgnitionAt,
      dismissDialogs: dismissIntroDialog,
  });

});
